/**
 * GraphQL Client Factory for Meridian Tools
 * Creates Apollo clients for server-side GraphQL operations
 * 
 * This is a simplified version that works within the Meridian package
 * without requiring imports from the Hub app
 */

import { ApolloClient, InMemoryCache, HttpLink, ApolloLink, concat } from "@apollo/client";

/**
 * Apollo Client 4.x requires consumers to declare which `errorPolicy` (and other
 * default option) literal types they intend to use, via this module augmentation.
 * Without this, Apollo's type machinery surfaces an error string instead of the
 * expected literal type. See:
 * https://www.apollographql.com/docs/react/data/typescript#declaring-default-options-for-type-safety
 *
 * Runtime behavior is unaffected — this only widens the allowed literal types
 * in `defaultOptions.watchQuery.errorPolicy` and `defaultOptions.query.errorPolicy`.
 */
declare module "@apollo/client" {
  namespace ApolloClient {
    namespace DeclareDefaultOptions {
      interface WatchQuery {
        errorPolicy: "all";
      }
      interface Query {
        errorPolicy: "all";
      }
    }
  }
}

/**
 * Create a server-side GraphQL client that uses the service role key
 * This bypasses RLS policies and should only be used for admin/system operations
 * 
 * DEPRECATED FOR TOOLS: Use createUserGraphQLClient() instead to respect RLS
 */
export function createServerGraphQLClient() {
  const httpLink = new HttpLink({
    uri: `${process.env.NEXT_PUBLIC_SUPABASE_URL}/graphql/v1`,
  });

  const authLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        apikey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
        // Use service role key to bypass RLS
        Authorization: `Bearer ${process.env.SUPABASE_SERVICE_ROLE_KEY}`,
      },
    }));

    return forward(operation);
  });

  return new ApolloClient({
    link: concat(authLink, httpLink),
    cache: new InMemoryCache(),
    ssrMode: true,
    defaultOptions: {
      watchQuery: {
        errorPolicy: "all",
      },
      query: {
        errorPolicy: "all",
      },
    },
  });
}

/**
 * Create a server-side GraphQL client that uses a user's authentication token
 * This respects RLS policies and should be used for tool execution
 * 
 * IMPORTANT: This client respects RLS! Use this for:
 * - Tool execution with user context
 * - User-scoped server-side operations
 * - Any operation that should respect user permissions
 */
export function createUserGraphQLClient(userToken: string) {
  const httpLink = new HttpLink({
    uri: `${process.env.NEXT_PUBLIC_SUPABASE_URL}/graphql/v1`,
  });

  const authLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        apikey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
        Authorization: `Bearer ${userToken}`,
      },
    }));

    return forward(operation);
  });

  return new ApolloClient({
    link: concat(authLink, httpLink),
    cache: new InMemoryCache(),
    ssrMode: true,
    defaultOptions: {
      watchQuery: {
        errorPolicy: "all",
      },
      query: {
        errorPolicy: "all",
      },
    },
  });
}
